import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../api/client';
import { SPV_FIELDS, apiError, fieldInput } from './AdminSpvs';

const ASSET_TYPES = 'ddjay_plot,delhi_pooling,flat';
const MILESTONES = 'sale_deed,jda_signed,licence,rera,launch,possession,exit';
const DOC_TYPES = 'title,licence,rera,valuation,pas4,sha,certificate,invoice,other';
const VISIBILITY = 'invited,shareholders,admin';

const AdminSpvEdit = () => {
  const { id } = useParams();
  const [spv, setSpv] = useState(null);
  const [vals, setVals] = useState({});
  const [assets, setAssets] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [docs, setDocs] = useState([]);
  const [asset, setAsset] = useState({ asset_type: '', location: '', area_sq_yd: '' });
  const [ms, setMs] = useState({ type: '', target_date: '' });
  const [doc, setDoc] = useState({ title: '', doc_type: '', visibility: 'invited', file: null });

  const load = useCallback(async () => {
    try {
      const [s, a, m, d] = await Promise.all([
        api.get(`/api/admin/spvs/${id}/`), api.get('/api/admin/assets/', { params: { spv: id } }),
        api.get('/api/admin/milestones/', { params: { spv: id } }), api.get('/api/admin/documents/', { params: { spv: id } }),
      ]);
      setSpv(s.data); setVals(s.data); setAssets(a.data); setMilestones(m.data); setDocs(d.data);
    } catch (e) {
      toast.error('Failed to load SPV');
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); await load(); } catch (e) { toast.error(apiError(e)); }
  };

  const save = (e) => {
    e.preventDefault();
    const body = {};
    SPV_FIELDS.forEach(([n]) => { body[n] = vals[n]; });
    act(() => api.patch(`/api/admin/spvs/${id}/`, body), 'Saved');
  };

  const upload = (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('spv', id);
    ['title', 'doc_type', 'visibility'].forEach((k) => fd.append(k, doc[k]));
    fd.append('file', doc.file);
    act(async () => { await api.post('/api/admin/documents/', fd); setDoc({ title: '', doc_type: '', visibility: 'invited', file: null }); }, 'Document uploaded');
  };

  if (!spv) return <main style={{ padding: '4rem 1rem', textAlign: 'center' }}><p>Loading…</p></main>;
  const live = spv.status !== 'draft';

  return (
    <main style={{ maxWidth: 900, margin: '0 auto', padding: '2rem 1rem' }}>
      <p><Link to="/admin/spvs">← SPVs</Link></p>
      <h1>{spv.name}</h1>
      <p>Status: <strong>{spv.status}</strong>{' '}
        {spv.allowed_next.map((n) => (
          <button key={n} onClick={() => window.confirm(`Move to "${n}"?`) && act(() => api.post(`/api/admin/spvs/${id}/transition/`, { status: n }), `Now ${n}`)}>
            Move to {n}
          </button>
        ))}
      </p>

      <p><Link to={`/admin/spvs/${id}/allotment`}>Allotment and register of members →</Link></p>

      <p><Link to={`/admin/spvs/${id}/distribution`}>Expenses and distribution →</Link></p>

      <h2>Terms and fees</h2>
      {live && <p role="note">Deal type, price and share counts are locked once the offer is live.</p>}
      <form onSubmit={save} style={{ display: 'grid', gap: 8, maxWidth: 520 }}>
        {SPV_FIELDS.map(([n, label, type]) => (
          <label key={n} htmlFor={n}>{label}{fieldInput(type, vals[n] ?? '', (e) => setVals({ ...vals, [n]: e.target.value }), n)}</label>
        ))}
        <button type="submit">Save</button>
      </form>

      <h2>Assets</h2>
      <ul>{assets.map((a) => (
        <li key={a.id}>{a.asset_type.replace('_', ' ')} — {a.location}{a.area_sq_yd && `, ${a.area_sq_yd} sq yd`}{' '}
          <button onClick={() => act(() => api.delete(`/api/admin/assets/${a.id}/`))}>Remove</button></li>
      ))}</ul>
      <form onSubmit={(e) => { e.preventDefault(); act(async () => { await api.post('/api/admin/assets/', { ...asset, spv: id, area_sq_yd: asset.area_sq_yd || null }); setAsset({ asset_type: '', location: '', area_sq_yd: '' }); }); }}
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {fieldInput(`select:${ASSET_TYPES}`, asset.asset_type, (e) => setAsset({ ...asset, asset_type: e.target.value }), 'asset_type')}
        <input aria-label="Location" placeholder="Location" value={asset.location} onChange={(e) => setAsset({ ...asset, location: e.target.value })} className="input" required />
        <input aria-label="Area (sq yd)" placeholder="Area (sq yd)" type="number" step="any" value={asset.area_sq_yd} onChange={(e) => setAsset({ ...asset, area_sq_yd: e.target.value })} className="input" />
        <button type="submit" disabled={!asset.asset_type}>Add asset</button>
      </form>

      <h2>Milestones</h2>
      <ul>{milestones.map((m) => (
        <li key={m.id}>{m.type.replace('_', ' ')} — target {m.target_date || '-'}, actual {m.actual_date || '-'} ({m.status}){' '}
          {m.status !== 'done' && <button onClick={() => act(() => api.patch(`/api/admin/milestones/${m.id}/`, { status: 'done', actual_date: new Date().toISOString().slice(0, 10) }))}>Mark done today</button>}{' '}
          <button onClick={() => act(() => api.delete(`/api/admin/milestones/${m.id}/`))}>Remove</button></li>
      ))}</ul>
      <form onSubmit={(e) => { e.preventDefault(); act(async () => { await api.post('/api/admin/milestones/', { spv: id, type: ms.type, target_date: ms.target_date || null }); setMs({ type: '', target_date: '' }); }); }}
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {fieldInput(`select:${MILESTONES}`, ms.type, (e) => setMs({ ...ms, type: e.target.value }), 'ms_type')}
        <input aria-label="Target date" type="date" value={ms.target_date} onChange={(e) => setMs({ ...ms, target_date: e.target.value })} className="input" />
        <button type="submit" disabled={!ms.type}>Add milestone</button>
      </form>

      <h2>Documents</h2>
      <ul>{docs.map((d) => (
        <li key={d.id}>{d.title} ({d.doc_type}, {d.visibility}) <button onClick={() => act(() => api.delete(`/api/admin/documents/${d.id}/`))}>Remove</button></li>
      ))}</ul>
      <form onSubmit={upload} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input aria-label="Document title" placeholder="Title" value={doc.title} onChange={(e) => setDoc({ ...doc, title: e.target.value })} className="input" required />
        {fieldInput(`select:${DOC_TYPES}`, doc.doc_type, (e) => setDoc({ ...doc, doc_type: e.target.value }), 'doc_type')}
        {fieldInput(`select:${VISIBILITY}`, doc.visibility, (e) => setDoc({ ...doc, visibility: e.target.value }), 'visibility')}
        <input aria-label="File" type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setDoc({ ...doc, file: e.target.files[0] })} required />
        <button type="submit" disabled={!doc.doc_type || !doc.file}>Upload</button>
      </form>
    </main>
  );
};

export default AdminSpvEdit;
